import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Key, Feather, TrendingUp, Users, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section 
        className="relative min-h-screen flex items-center justify-center overflow-hidden"
        style={{
          backgroundImage: 'url(https://images.pexels.com/photos/4058026/pexels-photo-4058026.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-5xl mx-auto px-4 text-center text-white">
          <div className="flex items-center justify-center mb-8">
            <Key className="w-16 h-16 text-amber-400" />
          </div>
          <h1 className="text-5xl md:text-7xl font-light tracking-tight leading-none mb-6">
            Author's Key
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Onde ideias viram livros.<br />
            Seu ateliê literário completo.
          </p>
          <p className="text-base md:text-lg text-white/80 mb-12 max-w-xl mx-auto">
            Organize, escreva, acompanhe e desenvolva suas obras com elegância e simplicidade.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/login" data-testid="cta-start-free">
              <Button size="lg" className="h-12 px-8 text-lg bg-amber-500 hover:bg-amber-600 text-stone-900 font-medium">
                Começar Grátis
              </Button>
            </Link>
            <Link to="/login" data-testid="cta-login">
              <Button size="lg" variant="outline" className="h-12 px-8 text-lg border-white text-white hover:bg-white hover:text-stone-900">
                Entrar
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-center mb-16">
            Tudo que você precisa para criar sua obra
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-lg border border-border/40 hover:shadow-md transition-all" data-testid="feature-writing">
              <Feather className="w-12 h-12 mx-auto mb-4 text-amber-500" />
              <h3 className="text-2xl font-medium mb-3">Escrita Confortável</h3>
              <p className="text-muted-foreground leading-relaxed">
                Editor elegante com salvamento automático, contador de palavras e foco total na sua criatividade.
              </p>
            </div>
            <div className="text-center p-8 rounded-lg border border-border/40 hover:shadow-md transition-all" data-testid="feature-organization">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-amber-500" />
              <h3 className="text-2xl font-medium mb-3">Organização Total</h3>
              <p className="text-muted-foreground leading-relaxed">
                Gerencie múltiplos livros, capítulos, inspirações, frases e post-its em um só lugar.
              </p>
            </div>
            <div className="text-center p-8 rounded-lg border border-border/40 hover:shadow-md transition-all" data-testid="feature-progress">
              <TrendingUp className="w-12 h-12 mx-auto mb-4 text-amber-500" />
              <h3 className="text-2xl font-medium mb-3">Acompanhamento</h3>
              <p className="text-muted-foreground leading-relaxed">
                Visualize seu progresso diário, sequência de escrita e evolução como escritor.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 px-4 bg-background">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-normal tracking-tight text-center mb-16">
            Comece grátis, evolua quando quiser
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {/* Free */}
            <div className="p-8 rounded-lg border-2 border-border/40" data-testid="plan-free">
              <h3 className="text-2xl font-medium mb-2">Gratuito</h3>
              <p className="text-muted-foreground mb-6">Tudo para começar a escrever</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Livros e capítulos ilimitados</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Editor completo com salvamento automático</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Baú de inspirações</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Chave do Dia motivacional</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Até 3 post-its por capítulo</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>3 Perguntas de Ouro por semana</span>
                </li>
              </ul>
            </div>

            {/* Premium */}
            <div className="p-8 rounded-lg border-2 border-amber-500 relative" data-testid="plan-premium">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-stone-900 px-4 py-1 rounded-full text-sm font-medium">
                Em Breve
              </div>
              <h3 className="text-2xl font-medium mb-2">Premium</h3>
              <p className="text-muted-foreground mb-6">Para escritores sérios</p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Tudo do plano gratuito</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Mentor Key com IA</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Perguntas de Ouro ilimitadas</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Post-its ilimitados</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Empresário Literário (metas e planejamento)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-1">✓</span>
                  <span>Análise contextual do seu livro</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 bg-card border-t border-border/40">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Key className="w-8 h-8 text-amber-500 mr-2" />
            <span className="text-xl font-serif">Author's Key</span>
          </div>
          <p className="text-muted-foreground mb-6">
            Seu ateliê literário. Onde ideias viram livros.
          </p>
          <p className="text-sm text-muted-foreground">
            © 2026 Author's Key. Todos os direitos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
